import { API_BASE_URL } from "../../../config";
import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useFetchUserSeller from '../../Utils/useFetchUserSeller';
import CustomerLink from '../../Components/CustomerLink';
import { regularCustomersFromByCategory } from '../../Utils/assignCustomerRoute';

const RoutineCustomers = () => {
    const navigate = useNavigate();

    const handleLogout = useCallback(() => {
        localStorage.removeItem("user");
        navigate("/login");
    }, [navigate]);

    const getToken = useCallback(() => {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        return user?.token || null;
    }, []);

    const rawSellers = useFetchUserSeller(handleLogout);
    const sellers = Array.isArray(rawSellers) ? rawSellers : [];

    const [selectedSeller, setSelectedSeller] = useState(null);
    const [routineList, setRoutineList] = useState([]);
    const [loadingRoutine, setLoadingRoutine] = useState(false);

    const [showAddModal, setShowAddModal] = useState(false);
    const [allCustomers, setAllCustomers] = useState([]);
    const [loadingCustomers, setLoadingCustomers] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [addingId, setAddingId] = useState(null);

    const safeRoutineList = Array.isArray(routineList) ? routineList : [];
    const safeAllCustomers = Array.isArray(allCustomers) ? allCustomers : [];

    const fetchRoutine = useCallback(async (sellerId) => {
        setLoadingRoutine(true);
        setRoutineList([]);
        try {
            const token = getToken();
            if (!token) { handleLogout(); return; }
            const resp = await fetch(`${API_BASE_URL}/customer/api/customer-buy-rutine/seller/${sellerId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (resp.status === 401 || resp.status === 403) { handleLogout(); return; }
            if (resp.ok) {
                const data = await resp.json();
                setRoutineList(Array.isArray(data) ? data : []);
            }
        } catch (e) {
            console.error("fetchRoutine error", e);
        } finally {
            setLoadingRoutine(false);
        }
    }, [getToken, handleLogout]);

    const handleSelectSeller = (seller) => {
        setSelectedSeller(seller);
        fetchRoutine(seller.uid);
    };

    const handleRemove = async (customerId) => {
        if (!window.confirm(`ลบลูกค้า ${customerId} ออกจากสาย ${selectedSeller?.nameuser || selectedSeller?.uid}?`)) return;
        try {
            const token = getToken();
            const resp = await fetch(`${API_BASE_URL}/customer/api/customer-buy-rutine/${selectedSeller.uid}/${customerId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (resp.ok) {
                setRoutineList(prev => (Array.isArray(prev) ? prev : []).filter(r => r.customerId !== customerId));
            } else {
                alert('ลบไม่สำเร็จ: ' + resp.status);
            }
        } catch (e) {
            alert('เกิดข้อผิดพลาด: ' + e.message);
        }
    };

    const fetchAllCustomers = useCallback(async () => {
        if (safeAllCustomers.length > 0) return;
        setLoadingCustomers(true);
        try {
            const token = getToken();
            const resp = await fetch(`${API_BASE_URL}/customer/api/customers/by-category`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ type: 'ALL' })
            });
            if (resp.ok) {
                const data = await resp.json();
                setAllCustomers(regularCustomersFromByCategory(data));
            }
        } catch (e) {
            console.error("fetchAllCustomers error", e);
        } finally {
            setLoadingCustomers(false);
        }
    }, [getToken, safeAllCustomers.length]);

    const openAddModal = () => {
        setShowAddModal(true);
        setSearchTerm('');
        fetchAllCustomers();
    };

    const handleAddCustomer = async (customer) => {
        const alreadyIn = safeRoutineList.some(r => r.customerId === customer.customerUid);
        if (alreadyIn) { alert('ลูกค้านี้อยู่ในสายแล้วครับ'); return; }
        setAddingId(customer.customerUid);
        try {
            const token = getToken();
            const resp = await fetch(`${API_BASE_URL}/customer/api/customer-buy-rutine/delivery-level/update`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ customerId: customer.customerUid, sellerId: selectedSeller.uid, deliveryLevel: 2 })
            });
            if (resp.ok) {
                setRoutineList(prev => [...(Array.isArray(prev) ? prev : []), {
                    customerId: customer.customerUid,
                    customerName: customer.storeName || customer.customerUid,
                    deliveryLevel: 2,
                    sellerId: selectedSeller.uid
                }]);
                setShowAddModal(false);
            } else {
                alert('เพิ่มไม่สำเร็จ: ' + resp.status);
            }
        } catch (e) {
            alert('เกิดข้อผิดพลาด: ' + e.message);
        } finally {
            setAddingId(null);
        }
    };

    const term = searchTerm.toLowerCase();
    const filteredCustomers = safeAllCustomers.filter(c =>
        (c.storeName || '').toLowerCase().includes(term) ||
        (c.customerUid || '').toLowerCase().includes(term)
    );

    const levelColor = (lv) => {
        if (lv >= 3) return '#2e7d32';
        if (lv === 2) return '#f57c00';
        return '#c62828';
    };

    return (
        <div style={{ padding: '20px', fontFamily: 'sans-serif' }}>
            <h2 style={{ marginBottom: '16px' }}>ลูกค้าประจำสาย</h2>

            <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
                {/* Seller list */}
                <div style={{ width: '220px', flexShrink: 0 }}>
                    <div style={{ fontWeight: 600, marginBottom: '8px', color: '#555' }}>รายการสาย</div>
                    {sellers.length === 0 && <div style={{ color: '#999', fontSize: '13px' }}>กำลังโหลด...</div>}
                    {sellers.map(s => (
                        <div
                            key={s.uid}
                            onClick={() => handleSelectSeller(s)}
                            style={{
                                padding: '9px 12px',
                                marginBottom: '4px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                background: selectedSeller?.uid === s.uid ? '#1565c0' : '#f0f4f8',
                                color: selectedSeller?.uid === s.uid ? '#fff' : '#333',
                                fontSize: '14px'
                            }}
                        >
                            <div style={{ fontWeight: 600 }}>{s.nameuser || s.username}</div>
                            <div style={{ fontSize: '11px', opacity: 0.75 }}>{s.uid}</div>
                        </div>
                    ))}
                </div>

                {/* Customer list */}
                <div style={{ flex: 1 }}>
                    {!selectedSeller && (
                        <div style={{ color: '#999', marginTop: '40px', textAlign: 'center' }}>
                            เลือกสายทางซ้ายเพื่อดูรายการลูกค้า
                        </div>
                    )}
                    {selectedSeller && (
                        <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                <div style={{ fontWeight: 600, fontSize: '16px' }}>
                                    สาย: {selectedSeller.nameuser || selectedSeller.uid}
                                    <span style={{ marginLeft: '8px', fontSize: '13px', color: '#888' }}>
                                        ({safeRoutineList.length} คน)
                                    </span>
                                </div>
                                <button
                                    onClick={openAddModal}
                                    style={{ padding: '7px 16px', background: '#2e7d32', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}
                                >
                                    + เพิ่มลูกค้า
                                </button>
                            </div>

                            {loadingRoutine && <div style={{ color: '#999' }}>กำลังโหลด...</div>}

                            {!loadingRoutine && safeRoutineList.length === 0 && (
                                <div style={{ color: '#999', marginTop: '20px', textAlign: 'center' }}>
                                    ยังไม่มีลูกค้าประจำสายนี้
                                </div>
                            )}

                            {!loadingRoutine && safeRoutineList.length > 0 && (
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                                    <thead>
                                        <tr style={{ background: '#e3eaf2' }}>
                                            <th style={th}>ชื่อร้าน</th>
                                            <th style={{ ...th, width: '80px', textAlign: 'center' }}>Level</th>
                                            <th style={{ ...th, width: '70px' }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {safeRoutineList.map((r, i) => (
                                            <tr key={r.customerId} style={{ background: i % 2 === 0 ? '#fff' : '#f9fbfc' }}>
                                                <td style={td}><CustomerLink name={r.customerName} code={r.customerId} /></td>
                                                <td style={{ ...td, textAlign: 'center' }}>
                                                    <span style={{ background: levelColor(r.deliveryLevel), color: '#fff', borderRadius: '12px', padding: '2px 10px', fontSize: '12px' }}>
                                                        {r.deliveryLevel}
                                                    </span>
                                                </td>
                                                <td style={td}>
                                                    <button
                                                        onClick={() => handleRemove(r.customerId)}
                                                        style={{ padding: '4px 10px', background: '#c62828', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                                                    >
                                                        ลบ
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Add customer modal */}
            {showAddModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div style={{ background: '#fff', borderRadius: '10px', padding: '24px', width: '480px', maxHeight: '70vh', display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                            <div style={{ fontWeight: 600, fontSize: '16px' }}>เพิ่มลูกค้าเข้าสาย</div>
                            <button onClick={() => setShowAddModal(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#555' }}>×</button>
                        </div>
                        <input
                            type="text"
                            placeholder="ค้นหาชื่อร้าน หรือรหัสลูกค้า..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            style={{ width: '100%', padding: '8px 12px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '14px', marginBottom: '12px', boxSizing: 'border-box' }}
                            autoFocus
                        />
                        {loadingCustomers && <div style={{ color: '#999', textAlign: 'center' }}>กำลังโหลด...</div>}
                        <div style={{ overflowY: 'auto', flex: 1 }}>
                            {filteredCustomers.slice(0, 100).map(c => {
                                const inList = safeRoutineList.some(r => r.customerId === c.customerUid);
                                return (
                                    <div key={c.customerUid} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 4px', borderBottom: '1px solid #f0f0f0' }}>
                                        <div>
                                            <div style={{ fontSize: '14px' }}><CustomerLink name={c.storeName} code={c.customerUid} /></div>
                                        </div>
                                        <button
                                            onClick={() => handleAddCustomer(c)}
                                            disabled={inList || addingId === c.customerUid}
                                            style={{ padding: '4px 12px', fontSize: '12px', background: inList ? '#bbb' : '#1565c0', color: '#fff', border: 'none', borderRadius: '4px', cursor: inList ? 'default' : 'pointer' }}
                                        >
                                            {inList ? 'มีแล้ว' : addingId === c.customerUid ? '...' : 'เพิ่ม'}
                                        </button>
                                    </div>
                                );
                            })}
                            {filteredCustomers.length === 0 && !loadingCustomers && (
                                <div style={{ color: '#999', textAlign: 'center', marginTop: '20px' }}>ไม่พบลูกค้า</div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const th = { padding: '8px 10px', textAlign: 'left', fontWeight: 600 };
const td = { padding: '8px 10px', verticalAlign: 'middle' };

export default RoutineCustomers;
